import { Box, Card, Flex, Grid, Text } from '@chakra-ui/react'
import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircleFlag } from 'react-circle-flags'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import AvatarCell from './base/AvatarCell'
import FilterButton from './base/FilterButton'
import { keepPreviousData, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { fetchEmployeeList, fetchTeamList } from '../../api/workforce/workforce'
import { fetchJobList } from '../../api/classification/classification'
import { Button, Icon, VStack } from '@chakra-ui/react'
import { ExportIcon } from '../../assets/icons'
import handleToast from '../HandleToast'
import { exportEmployeesToCsv, exportEmployeesToXls } from '../../api/workforce/workforce'
import { useToast } from '@chakra-ui/react'

const PayrollTable: React.FC = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const itemsPerPage = 15
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [managerFilterSearch, setManagerFilterSearch] = useState('')
  const [contractTypeFilterSearch, setContractTypeFilterSearch] = useState('')
  const toast = useToast()
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [selectedRows, setSelectedRows] = useState<(number | string)[]>([])

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])

  const people = useQuery({
    queryKey: ['payroll-people', page, searchTerm, filtersKey],
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchEmployeeList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters: { ...filters, active: ['true'] },
        // Only request the fields needed to render the payroll table columns
        expand: 'teams.id,teams.name,managed_teams,current_position',
        fields:
          'id,first_name,last_name,full_name,profile_picture,contract_type,current_position.id,current_position.title,teams.id,teams.name,managed_teams.id,managed_teams.name,contract_type,country_of_work,salary,fte,total_benefits',
      }),
  })

  useEffect(() => {
    const currentCount = people.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['payroll-people', nextPage, searchTerm, filtersKey],
      staleTime: 60 * 1000,
      queryFn: () =>
        fetchEmployeeList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters: { ...filters, active: ['true'] },
          expand: 'teams.id,teams.name,managed_teams,current_position',
          fields:
            'id,first_name,last_name,full_name,profile_picture,contract_type,current_position.id,current_position.title,teams.id,teams.name,managed_teams.id,managed_teams.name,contract_type,country_of_work,salary,fte,total_benefits',
        }),
    })
  }, [people.data, page, searchTerm, filters, filtersKey, itemsPerPage, queryClient])

  // --- Filter: Teams (infinite scroll) ---
  const [teamsFilterSearch, setTeamsFilterSearch] = useState('')
  const teamsFilterQuery = useInfiniteQuery({
    queryKey: ['filter-teams', 10, teamsFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchTeamList({ search: teamsFilterSearch, page: pageParam, pageSize: 10, filters: {} }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
  })
  const teamsFilterList = useMemo(
    () =>
      (teamsFilterQuery.data?.pages ?? []).flatMap((p: any) =>
        Array.isArray(p?.data) ? p.data.map((t: any) => ({ id: t.id, name: t.name })) : [],
      ),
    [teamsFilterQuery.data?.pages],
  )

  // --- Filter: Job Title (infinite scroll) ---
  const [positionsFilterSearch, setPositionsFilterSearch] = useState('')
  const positionsFilterQuery = useInfiniteQuery({
    queryKey: ['filter-positions', 10, positionsFilterSearch],
    queryFn: ({ pageParam }) => fetchJobList({ search: positionsFilterSearch, page: pageParam, pageSize: 10 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
  })
  const positionsFilterList = useMemo(
    () =>
      (positionsFilterQuery.data?.pages ?? []).flatMap((p: any) =>
        Array.isArray(p?.data) ? p.data.map((pItem: any) => ({ id: pItem.id, name: pItem.title })) : [],
      ),
    [positionsFilterQuery.data?.pages],
  )

  const contractTypeFilterList = useMemo(
    () => [
      { id: 'CONTRACT_FT', name: t('workforce.mutations.contractTypes.fullTime') },
      { id: 'CONTRACT_PT', name: t('workforce.mutations.contractTypes.partTime') },
      { id: 'CONTRACT_IN', name: t('workforce.mutations.contractTypes.internship') },
    ],
    [t],
  )

  // --- Filter: Country (static) ---
  const countryFilterList = useMemo(() => phoneNumbers.map((c) => ({ id: c.code, name: c.name })), [])

  const exportPayrollToXls = async () => {
    try {
      const exportIds =
        selectedRows.length > 0 &&
        selectedRows.length !== (people?.data?.data?.length || 0)
          ? selectedRows
          : undefined

      setIsExporting(true)

      await exportEmployeesToXls({
        search: searchTerm,
        filters: { ...filters, active: ['true'] },
        ids: exportIds,
      })

      handleToast(
        toast,
        true,
        t('workforce.messages.exportSuccess'),
        t('workforce.messages.exportSuccessMessage', { format: 'XLS' }),
      )
    } catch (e) {
      console.error(e)
      handleToast(toast, false, t('workforce.messages.exportFailed'), t('workforce.messages.exportFailedMessage'))
    } finally {
      setIsExporting(false)
    }
  }

  const exportPayrollToCsv = async () => {
    try {
      const exportIds =
        selectedRows.length > 0 &&
        selectedRows.length !== (people?.data?.data?.length || 0)
          ? selectedRows
          : undefined
      
          setIsExporting(true)

      await exportEmployeesToCsv({
        search: searchTerm,
        filters: { ...filters, active: ['true'] },
        ids: exportIds,
      })

      handleToast(
        toast,
        true,
        t('workforce.messages.exportSuccess'),
        t('workforce.messages.exportSuccessMessage', { format: 'CSV' }),
      )
    } catch (e) {
      console.error(e)
      handleToast(toast, false, t('workforce.messages.exportFailed'), t('workforce.messages.exportFailedMessage'))
    } finally {
      setIsExporting(false)
    }
  }

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.fields.name'),
      accessor: 'name',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <AvatarCell image={row.profile_picture} name={`${row.first_name} ${row.last_name}`} />
      ),
    },

    {
      label: t('common.fields.jobTitle'),
      accessor: 'current_position',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.current_position?.title || t('common.labels.na')}
        </Text>
      ),
    },
    {
      label: t('payroll.table.columns.teams'),
      accessor: 'teams',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const membershipTeamNames = row.teams?.map((team: { name: string }) => team.name) || []
        const managedTeamNames = row.managed_teams?.map((team: { name: string }) => team.name) || []
        const teamNames = Array.from(new Set([...membershipTeamNames, ...managedTeamNames]))
        
        return (
          <Text textStyle="l-xs" isTruncated>
            {teamNames.length > 1 ? (
              <>
                {teamNames.slice(0, 1).join(', ')}
                <Text as="span" color="solid.400" textStyle="l-xs" ml="8px">
                  {t('common.labels.moreCount', { count: teamNames.length - 1 })}
                </Text>
              </>
            ) : (
              teamNames.length !== 0 ? teamNames.join(', ') : t('common.labels.na')
            )}
          </Text>
        )
      },
    },
    {
      label: t('payroll.table.columns.contractType'),
      accessor: 'contract_type',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.contract_type === 'FT'
            ? t('workforce.mutations.contractTypes.fullTime')
            : row.contract_type === 'PT'
              ? t('workforce.mutations.contractTypes.partTime')
              : row.contract_type === 'IN'
                ? t('workforce.mutations.contractTypes.internship')
                : t('common.labels.na')}
        </Text>
      ),
    },
    {
      label: t('workforce.people.countryOfWork'),
      accessor: 'country_of_work',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Flex alignItems="center">
          {row.country_of_work && (
            <CircleFlag
              countryCode={row.country_of_work?.toLowerCase()}
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          )}
          <Text isTruncated textStyle="l-xs">
            {row?.country_of_work ? getCountryNameByCode(row?.country_of_work) : t('common.labels.na')}
          </Text>
        </Flex>
      ),
    },
    {
      label: t('payroll.table.columns.salary'),
      accessor: 'salary',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          €{row.salary ? row.salary.toLocaleString() : '0'}
          <Text as="span" color="solid.400" ml="1px">
            {t('payroll.units.perMonth')}
          </Text>
        </Text>
      ),
    },

    {
      label: t('payroll.table.columns.fte'),
      accessor: 'fte',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          {row.fte ? row.fte : '0'}%
        </Text>
      ),
    },
    {
      label: t('payroll.table.columns.benefits'),
      accessor: 'benefits',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" isTruncated>
          €{row.total_benefits ? row.total_benefits.toLocaleString() : '0'}
          <Text as="span" color="solid.400" ml="1px">
            {t('payroll.units.perMonth')}
          </Text>
        </Text>
      ),
    },
    // { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleRowClick = (row: RowData) => {
    navigate(`/user/workforce/people/${row.id}`, { state: { tab: 'compensation' } })
  }

  const handleSelectRow = (id: number | string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows((prev) => [...prev, id])
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id))
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    const tableData = people?.data?.data || []

    if (isChecked) {
      setSelectedRows(tableData.map((row: RowData) => row.id))
    } else {
      setSelectedRows([])
    }
  }

  const managersFilterQuery = useInfiniteQuery({
    queryKey: ['filter-managers', 10, managerFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({
        search: managerFilterSearch,
        page: pageParam,
        pageSize: 10,
        filters: { only_primary_evaluators: ['true'] },
        minimal: true,
        expand: '',
        fields: 'id,first_name,last_name,full_name',
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
  })

  const managersFilterList = useMemo(
    () =>
      (managersFilterQuery.data?.pages ?? []).flatMap((p: any) =>
        Array.isArray(p?.data)
          ? p.data.map((m: any) => ({
              id: m.id,
              name: m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
            }))
          : [],
      ),
    [managersFilterQuery.data?.pages],
  )

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput
              onSearch={(term) => {
                setSearchTerm(term)
                setPage(1)
              }}
            />
            <Flex gap="8px">
              <FilterButton
                label={t('common.fields.manager')}
                options={managersFilterList}
                onApply={(selected) => updateFilter('primary_evaluator', selected)}
                search={managerFilterSearch}
                setSearch={setManagerFilterSearch}
                onLoadMore={() => managersFilterQuery.fetchNextPage()}
                hasMore={!!managersFilterQuery.hasNextPage}
                isLoadingMore={managersFilterQuery.isFetchingNextPage}
                isLoading={managersFilterQuery.isFetching && !managersFilterQuery.isFetchingNextPage}
              />
              <FilterButton
                label={t('common.fields.contractType')}
                options={contractTypeFilterList}
                onApply={(selected) => {
                  // Map CONTRACT_FT -> FT, CONTRACT_PT -> PT, CONTRACT_IN -> IN
                  const apiValues = selected.map((s) => s.replace('CONTRACT_', ''))
                  updateFilter('contract_type', apiValues)
                }}
                search={contractTypeFilterSearch}
                setSearch={setContractTypeFilterSearch}
              />
              <FilterButton
                label={t('common.fields.team')}
                options={teamsFilterList}
                onApply={(selected) => updateFilter('teams', selected)}
                search={teamsFilterSearch}
                setSearch={setTeamsFilterSearch}
                onLoadMore={() => teamsFilterQuery.fetchNextPage()}
                hasMore={!!teamsFilterQuery.hasNextPage}
                isLoadingMore={teamsFilterQuery.isFetchingNextPage}
                isLoading={teamsFilterQuery.isFetching && !teamsFilterQuery.isFetchingNextPage}
              />
              <FilterButton
                label={t('common.fields.jobTitle')}
                options={positionsFilterList}
                onApply={(selected) => updateFilter('current_position', selected)}
                search={positionsFilterSearch}
                setSearch={setPositionsFilterSearch}
                onLoadMore={() => positionsFilterQuery.fetchNextPage()}
                hasMore={!!positionsFilterQuery.hasNextPage}
                isLoadingMore={positionsFilterQuery.isFetchingNextPage}
                isLoading={positionsFilterQuery.isFetching && !positionsFilterQuery.isFetchingNextPage}
              />
              <FilterButton
                label={t('common.fields.country')}
                options={countryFilterList}
                onApply={(selected) => updateFilter('country_of_work', selected)}
              />
              {/* Export button */}
              <Box position="relative">
                <Button
                  variant="white"
                  onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                  disabled={!people?.data?.data?.length || isExporting}
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="black" mr={2}>
                    <ExportIcon />
                  </Icon>
                  {isExporting
                    ? t('common.states.exporting')
                    : selectedRows.length > 0
                      ? t('workforce.people.exportSelected', { count: selectedRows.length })
                      : t('workforce.people.exportAll')}
                </Button>

                {isExportDropdownOpen && (
                  <Box
                    position="absolute"
                    right="0"
                    width="196px"
                    height="92px"
                    borderRadius="8px"
                    mt={2}
                    boxShadow="0px 2px 8px -2px #00000014, 0px 2px 14px -3px #0000001F"
                    backgroundColor="white"
                    zIndex="1000"
                  >
                    <VStack align="stretch" spacing={0} height="100%">
                      <Button
                        variant="dropdown"
                        height="50%"
                        borderRadius="8px 8px 0 0"
                        onClick={exportPayrollToXls}
                        fontSize="sm"
                      >
                        {t('workforce.people.exportAsXLS')}
                      </Button>
                      <Button
                        variant="dropdown"
                        height="50%"
                        borderRadius="0 0 8px 8px"
                        onClick={exportPayrollToCsv}
                        fontSize="sm"
                      >
                        {t('workforce.people.exportAsCSV')}
                      </Button>
                    </VStack>
                  </Box>
                )}
              </Box>
            </Flex>
          </Flex>
          {people.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={people?.data?.data || []}
                onRowClick={handleRowClick}
                onOpenNewTab={(row: RowData) => {
                  const path = `/user/workforce/people/${row.id}?tab=compensation`
                  window.open(path, '_blank')
                }}
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                allSelected={
                  people?.data?.data?.length > 0 && selectedRows.length === (people?.data?.data?.length || 0)
                }
                isIndeterminate={selectedRows.length > 0 && selectedRows.length < (people?.data?.data?.length || 0)}
                tableType="people"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={people?.data?.max_page || 0}
            totalItems={people?.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Box>
      </Grid>
    </Card>
  )
}

export default PayrollTable
